import React, { useCallback, useEffect, useMemo, useState } from 'react'
import type { ColDef } from 'ag-grid-community'
import DataGrid from '@/components/data/DataGrid'
import { designTokens } from '@/utils/design-tokens'
import { useShowTasks } from '@/hooks/useShowTasks'
import { formatDateForAPI } from '@/components/ui/DateRangePicker/utils'
import { TaskSummaryService, type TaskSummaryTotalResponse } from '@/api'
import type { SummaryListPanelType } from './SummaryListPanels'

interface PeriodSummaryTableProps {
  workspaceId?: string
  /** 조회 범위 (스냅샷 시점은 X-As-Of 헤더로 별도 전달) */
  startDate?: Date | null
  endDate?: Date | null
  /** 부서 필터 (선택 시 해당 부서로 요약을 좁힘) */
  departmentId?: string
  /** 사업 필터 (선택 시 해당 사업으로 요약을 좁힘) */
  businessId?: string
  /** 행 클릭 시 해당 항목의 목록 패널 열기 */
  onItemClick?: (type: SummaryListPanelType, itemName: string) => void
}

interface SummaryDataItem {
  id: string
  itemName: '세부실행과제' | '수행업무' | '예산' | '성과'
  total: number
  completed: number
  incomplete: number
  ratio: number
  unit?: string
}

const ITEM_NAME_TO_TYPE: Record<SummaryDataItem['itemName'], SummaryListPanelType> = {
  '수행업무': 'task_item',
  '세부실행과제': 'task',
  '예산': 'budget',
  '성과': 'performance',
}

const formatNumber = (value: number, unit?: string) => {
  if (unit === '원') return `${new Intl.NumberFormat('ko-KR').format(value)}${unit}`
  return `${new Intl.NumberFormat('ko-KR').format(value)}개`
}

const mapApiToSummaryData = (api: TaskSummaryTotalResponse): SummaryDataItem[] => [
  {
    id: '1',
    itemName: '수행업무',
    total: api.task_item.total.total_count ?? 0,
    completed: api.task_item.total.completed_count ?? 0,
    incomplete: api.task_item.total.incomplete_count ?? 0,
    ratio: api.task_item.total.count_percentage ?? 0,
  },
  {
    id: '2',
    itemName: '세부실행과제',
    total: api.task.total.total_count ?? 0,
    completed: api.task.total.completed_count ?? 0,
    incomplete: api.task.total.incomplete_count ?? 0,
    ratio: api.task.total.count_percentage ?? 0,
  },
  {
    id: '3',
    itemName: '예산',
    total: api.task_budget.total.total_target_figure ?? 0,
    completed: api.task_budget.total.total_done_figure ?? 0,
    incomplete: api.task_budget.total.total_left_figure ?? 0,
    ratio: api.task_budget.total.figure_percentage ?? 0,
    unit: '원',
  },
  {
    id: '4',
    itemName: '성과',
    total: api.task_performance.total.total_count ?? 0,
    completed: api.task_performance.total.completed_count ?? 0,
    incomplete: api.task_performance.total.incomplete_count ?? 0,
    ratio: api.task_performance.total.count_percentage ?? 0,
  },
]

// 항목명 셀 — 점 + 이름
const ItemNameRenderer = (params: any) => (
  <div className="flex items-center gap-2 h-full">
    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: designTokens.colors.gray[4] }} />
    <span
      style={{
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: designTokens.typography.fontSize['body-medium'][0],
        color: designTokens.colors.gray[6],
      }}
    >
      {params.value}
    </span>
  </div>
)

// 진행율 막대 그래프 셀
const ProgressBarRenderer = (params: any) => {
  const ratio = params.value
  if (typeof ratio !== 'number') return '-'
  return (
    <div className="flex items-center gap-3 h-full w-full">
      <div
        className="flex-1"
        style={{
          height: '22px',
          backgroundColor: '#D0D0D0',
          borderRadius: '6px',
          position: 'relative',
          overflow: 'hidden',
          minWidth: '200px',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100%',
            width: `${ratio}%`,
            backgroundColor: '#2F4062',
            borderRadius: '6px',
            transition: 'width 0.3s ease',
          }}
        />
      </div>
    </div>
  )
}

/** 주간/월간 업무 진행 현황 — 상세 현황의 요약 테이블 (선택 기간 기준) */
const PeriodSummaryTable: React.FC<PeriodSummaryTableProps> = ({
  workspaceId,
  startDate,
  endDate,
  departmentId,
  businessId,
  onItemClick,
}) => {
  const [summary, setSummary] = useState<TaskSummaryTotalResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchSummary = useCallback(async () => {
    if (!workspaceId) return
    setIsLoading(true)
    setError(null)
    try {
      // 스냅샷 시점은 X-As-Of 헤더, 조회 범위는 start/end로 전달
      const response = await TaskSummaryService.appsTaskViewsGetTaskSummaryTotal({
        workspaceId,
        departmentId: departmentId || undefined,
        businessId: businessId || undefined,
        startDate: startDate ? formatDateForAPI(startDate) : undefined,
        endDate: endDate ? formatDateForAPI(endDate) : undefined,
      })
      setSummary(response)
    } catch (err) {
      console.error('Failed to fetch task summary:', err)
      setError('요약 정보를 불러오는데 실패했습니다.')
      setSummary(null)
    } finally {
      setIsLoading(false)
    }
  }, [workspaceId, departmentId, businessId, startDate, endDate])

  useEffect(() => { fetchSummary() }, [fetchSummary])

  // 워크스페이스 설정에서 세부실행과제 표시가 OFF 면 세부실행과제 행을 뺀다
  const showTasks = useShowTasks()

  const gridData = useMemo(() => {
    if (!summary) return []
    return mapApiToSummaryData(summary).filter(item => showTasks || item.itemName !== '세부실행과제').map(item => ({
      ...item,
      totalFormatted: formatNumber(item.total, item.unit),
      completedFormatted: formatNumber(item.completed, item.unit),
      incompleteFormatted: formatNumber(item.incomplete, item.unit),
    }))
  }, [summary, showTasks])

  const columnDefs: ColDef[] = useMemo(() => [
    {
      headerName: '항목명',
      field: 'itemName',
      width: 150, minWidth: 150, maxWidth: 150,
      cellRenderer: ItemNameRenderer,
      sortable: false, filter: false, suppressSizeToFit: true,
    },
    {
      headerName: '전체', field: 'totalFormatted',
      width: 180, minWidth: 180, maxWidth: 180,
      sortable: false, filter: false, suppressSizeToFit: true,
      cellStyle: { textAlign: 'right', padding: '0 16px' },
    },
    {
      headerName: '완료', field: 'completedFormatted',
      width: 180, minWidth: 180, maxWidth: 180,
      sortable: false, filter: false, suppressSizeToFit: true,
      cellStyle: { textAlign: 'right', padding: '0 16px' },
    },
    {
      headerName: '미완료', field: 'incompleteFormatted',
      width: 180, minWidth: 180, maxWidth: 180,
      sortable: false, filter: false, suppressSizeToFit: true,
      cellStyle: { textAlign: 'right', padding: '0 16px' },
    },
    {
      headerName: '비율', field: 'ratio',
      width: 100, minWidth: 100, maxWidth: 100,
      sortable: false, filter: false, suppressSizeToFit: true,
      valueFormatter: (p) => p.value != null ? `${p.value}%` : '-',
    },
    {
      headerName: '그래프', field: 'ratio', colId: 'graph',
      flex: 1, minWidth: 280,
      cellRenderer: ProgressBarRenderer,
      sortable: false, filter: false, suppressSizeToFit: false,
    },
  ], [])

  const handleRowClick = useCallback((event: any) => {
    const itemName = event.data?.itemName as SummaryDataItem['itemName'] | undefined
    if (!itemName || !onItemClick) return
    onItemClick(ITEM_NAME_TO_TYPE[itemName], itemName)
  }, [onItemClick])

  if (error) {
    return (
      <div
        className="flex items-center justify-center h-32 text-center"
        style={{
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: designTokens.typography.fontSize['body-medium'][0],
          color: designTokens.colors.gray[4],
        }}
      >
        {error}
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border" style={{ borderColor: designTokens.colors.gray[1] }}>
      <div className="w-full">
        <DataGrid
          rowData={gridData}
          columnDefs={columnDefs}
          loading={isLoading}
          headerHeight={40}
          rowHeight={42}
          height={40 + (Math.max(gridData.length, 1) * 42) + 2}
          suppressRowClickSelection={true}
          animateRows={true}
          onRowClicked={handleRowClick}
          getRowStyle={(params) => ({
            backgroundColor: (params.node.rowIndex! % 2 === 0)
              ? designTokens.colors.gray[0]
              : designTokens.colors.white,
            cursor: onItemClick ? 'pointer' : 'default',
          })}
          defaultColDef={{ sortable: false, filter: false, resizable: false, suppressSizeToFit: true }}
          enableRangeSelection={true}
          rangeSelectionExcludeColumns={['graph']}
          enableCellCopy={true}
          onGridReady={(params) => params.api.sizeColumnsToFit()}
        />
      </div>
    </div>
  )
}

export default PeriodSummaryTable
